import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// Phase 9 — Lightweight in-app onboarding tour.
//
// Renders a fixed-position popover anchored to a `[data-tour-id="..."]`
// element on the page, with a soft pulsing outline around the anchor so
// it's obvious what the popover is pointing at. Non-blocking — the user
// can keep interacting with the page if they want.
//
// No backdrop dimmer, no spotlight cutout: intro.js feels heavy in-app,
// and product tours like Linear / Vercel land lighter. If the anchor
// selector doesn't resolve (e.g. no comments yet for the Reply/Edit
// steps), the popover renders centred without an outline — still useful
// as forward-looking instruction.

export type TourStep = {
  id: string;
  /** Selector matched against `document.querySelector`. Pass `null` for a
   *  centred popover with no anchor highlight (good for intro / outro). */
  anchorSelector: string | null;
  title: string;
  body: string;
};

type Props = {
  steps: TourStep[];
  onClose: () => void;
  onComplete: () => void;
};

const POPOVER_WIDTH = 300;
const POPOVER_OFFSET = 12; // gap between anchor edge and popover
const HIGHLIGHT_CLASS = "clarity-tour-highlight";

type PopoverPosition = {
  top: number;
  left: number;
  placement: "below" | "above" | "right" | "left" | "centre";
};

export function OnboardingTour({ steps, onClose, onComplete }: Props) {
  const [index, setIndex] = useState(0);
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const step = steps[index];
  const isLast = index === steps.length - 1;

  // Esc closes the tour. Listen on window so it works while focus is
  // anywhere on the page.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Inject the pulse keyframes once. Lives at document level so the
  // highlighted element doesn't need its own animation rule.
  useEffect(() => {
    ensurePulseKeyframes();
  }, []);

  // Update the anchor outline + popover position whenever the step
  // changes, and keep them in sync on resize / scroll.
  const layout = useCallback(() => {
    if (!step) return;
    const anchor = step.anchorSelector
      ? (document.querySelector(step.anchorSelector) as HTMLElement | null)
      : null;
    setPosition(computePosition(anchor, popoverRef.current));
    // Toggle the highlight class on the anchor — `paintPins` and other
    // re-renders are fine, we just clean up on step change.
    document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => {
      el.classList.remove(HIGHLIGHT_CLASS);
    });
    if (anchor) anchor.classList.add(HIGHLIGHT_CLASS);
  }, [step]);

  useLayoutEffect(() => {
    layout();
  }, [layout]);

  useEffect(() => {
    let ticking = false;
    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        layout();
      });
    };
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [layout]);

  // Final cleanup on unmount — drop the highlight class so it doesn't
  // stick around past the tour's lifecycle.
  useEffect(() => {
    return () => {
      document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => {
        el.classList.remove(HIGHLIGHT_CLASS);
      });
    };
  }, []);

  function next() {
    if (isLast) {
      onComplete();
    } else {
      setIndex((i) => i + 1);
    }
  }

  function back() {
    setIndex((i) => Math.max(0, i - 1));
  }

  if (!step || !position) {
    // First-render layout pass: render an off-screen popover so we can
    // measure it for centring. Subsequent passes have a real position.
    return (
      <div
        ref={popoverRef}
        aria-hidden
        style={{
          position: "fixed",
          top: -9999,
          left: -9999,
          width: POPOVER_WIDTH,
          visibility: "hidden",
        }}
      >
        {step ? <PopoverCard step={step} index={index} total={steps.length} onBack={back} onNext={next} onSkip={onClose} isLast={isLast} /> : null}
      </div>
    );
  }

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={`Onboarding step ${index + 1} of ${steps.length}: ${step.title}`}
      className="fixed z-[2147483645]"
      style={{
        top: position.top,
        left: position.left,
        width: POPOVER_WIDTH,
      }}
    >
      <PopoverCard
        step={step}
        index={index}
        total={steps.length}
        onBack={back}
        onNext={next}
        onSkip={onClose}
        isLast={isLast}
      />
    </div>
  );
}

function PopoverCard({
  step,
  index,
  total,
  onBack,
  onNext,
  onSkip,
  isLast,
}: {
  step: TourStep;
  index: number;
  total: number;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  isLast: boolean;
}) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-[0_24px_60px_-16px_rgba(15,15,15,0.25),0_8px_24px_-12px_rgba(15,15,15,0.12)]">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-neutral-400">
          {index + 1} of {total}
        </span>
        <button
          type="button"
          onClick={onSkip}
          className="text-[11px] text-neutral-500 hover:text-neutral-900"
        >
          Skip tour
        </button>
      </div>
      <h3 className="mt-2 text-sm font-semibold text-neutral-900">{step.title}</h3>
      <p className="mt-1.5 text-xs leading-relaxed text-neutral-600">{step.body}</p>
      <div className="mt-4 flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={index === 0}
          className="text-[11px] text-neutral-500 hover:text-neutral-900 disabled:opacity-0"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="rounded-md px-3 py-1.5 text-xs font-medium shadow-sm"
          style={{ background: "#1AE07D", color: "#0a2a17" }}
        >
          {isLast ? "Got it" : "Next →"}
        </button>
      </div>
    </div>
  );
}

// ---------- positioning ----------

function computePosition(
  anchor: HTMLElement | null,
  popoverEl: HTMLElement | null,
): PopoverPosition {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const popH = popoverEl?.offsetHeight ?? 160; // measured after first paint

  if (!anchor) {
    // No anchor — centre the popover both axes.
    return {
      top: Math.max(16, (vh - popH) / 2),
      left: Math.max(16, (vw - POPOVER_WIDTH) / 2),
      placement: "centre",
    };
  }

  const rect = anchor.getBoundingClientRect();

  // Try placements in order: below, above, right, left. Fall back to
  // centre when nothing fits (e.g. anchor fills the viewport).
  const spaceBelow = vh - rect.bottom;
  const spaceAbove = rect.top;
  const spaceRight = vw - rect.right;
  const spaceLeft = rect.left;

  if (spaceBelow >= popH + POPOVER_OFFSET + 16) {
    // Centre horizontally on the anchor when there's room.
    const left = clamp(
      rect.left + rect.width / 2 - POPOVER_WIDTH / 2,
      16,
      vw - POPOVER_WIDTH - 16,
    );
    return { top: rect.bottom + POPOVER_OFFSET, left, placement: "below" };
  }
  if (spaceAbove >= popH + POPOVER_OFFSET + 16) {
    const left = clamp(
      rect.left + rect.width / 2 - POPOVER_WIDTH / 2,
      16,
      vw - POPOVER_WIDTH - 16,
    );
    return { top: rect.top - popH - POPOVER_OFFSET, left, placement: "above" };
  }
  if (spaceRight >= POPOVER_WIDTH + POPOVER_OFFSET + 16) {
    const top = clamp(rect.top + rect.height / 2 - popH / 2, 16, vh - popH - 16);
    return { top, left: rect.right + POPOVER_OFFSET, placement: "right" };
  }
  if (spaceLeft >= POPOVER_WIDTH + POPOVER_OFFSET + 16) {
    const top = clamp(rect.top + rect.height / 2 - popH / 2, 16, vh - popH - 16);
    return {
      top,
      left: rect.left - POPOVER_WIDTH - POPOVER_OFFSET,
      placement: "left",
    };
  }

  // Centre fallback — useful when the anchor is the iframe (fills the
  // available area).
  return {
    top: Math.max(16, (vh - popH) / 2),
    left: Math.max(16, (vw - POPOVER_WIDTH) / 2),
    placement: "centre",
  };
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

// ---------- pulse keyframes ----------

function ensurePulseKeyframes() {
  const id = "clarity-tour-style";
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = `
    @keyframes clarity-tour-pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(26, 224, 125, 0.6), 0 0 0 2px #1AE07D; }
      50%       { box-shadow: 0 0 0 8px rgba(26, 224, 125, 0), 0 0 0 2px #1AE07D; }
    }
    .${HIGHLIGHT_CLASS} {
      position: relative;
      border-radius: 6px;
      animation: clarity-tour-pulse 1.6s ease-in-out infinite;
      outline-offset: 2px;
      z-index: 1;
    }
  `;
  document.head.appendChild(style);
}
